import React, { useEffect, useState } from 'react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { Building2, Edit3, Save, X, Search, CheckCircle, AlertCircle, ShieldAlert } from 'lucide-react';

const EMPTY = {
    entity_type: 'SRL',
    legal_name: '',
    cui: '',
    trade_register: '',
    address_country: 'RO',
    county: '',
    city: '',
    street: '',
    postal_code: '',
    bank_iban: '',
    billing_email: '',
    billing_phone: '',
};

const ENTITY_OPTIONS = ['SRL', 'SA', 'PFA'];

// Fields whose change requires the user to confirm with their password — must
// match the backend rule in PUT /r/billing exactly.
const FISCAL_FIELDS = ['cui', 'legal_name', 'trade_register'];

function Row({ children, cols = 2 }) {
    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fit, minmax(${cols === 1 ? '100%' : '220px'}, 1fr))`,
            gap: 12,
            marginBottom: 12,
        }}>{children}</div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="label">{label}</label>
            {children}
            {hint && <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)', marginTop: 4 }}>{hint}</div>}
        </div>
    );
}

function ReadOnlyRow({ label, value }) {
    return (
        <div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>{label}</div>
            <div style={{ fontSize: '0.95rem', color: value ? 'var(--text)' : 'var(--text-faint)' }}>
                {value || '—'}
            </div>
        </div>
    );
}

export function BillingProfileEditor() {
    const { anafLookup } = useAuth();
    const [data, setData] = useState(null);     // current saved billing from server
    const [form, setForm] = useState(EMPTY);    // form state during edit
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [anafLoading, setAnafLoading] = useState(false);
    const [anafMsg, setAnafMsg] = useState('');
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data: resp } = await api.get('/r/billing');
                if (cancelled) return;
                const b = resp?.billing || {};
                setData(b);
                setForm({ ...EMPTY, ...b });
                // Auto-open the editor on first load when fiscal data is missing so
                // the user can't miss it — same reason the alert panel was added in
                // Plan & facturare. They hit this tab because checkout sent them.
                if (!b?.cui || !b?.legal_name) {
                    setEditing(true);
                    setInfo('Completează datele firmei ca să poți emite facturi și să faci plata abonamentului.');
                }
            } catch (e) {
                if (!cancelled) setError(formatApiError(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const startEdit = () => {
        setForm({ ...EMPTY, ...(data || {}) });
        setError(''); setInfo(''); setAnafMsg('');
        setEditing(true);
    };

    const cancelEdit = () => {
        setEditing(false);
        setError(''); setInfo(''); setAnafMsg('');
        setForm({ ...EMPTY, ...(data || {}) });
    };

    const runAnaf = async () => {
        if (!form.cui?.trim()) {
            setAnafMsg('Introdu CUI-ul mai întâi.');
            return;
        }
        setAnafLoading(true); setAnafMsg('');
        try {
            const resp = await anafLookup(form.cui.trim());
            setAnafMsg(resp.is_active ? 'Date completate automat din registrul ANAF.' : 'Atenție: firma e marcată INACTIVĂ în ANAF.');
            const fullStreet = (resp.street || '') + (resp.street_no ? ' ' + resp.street_no : '');
            setForm(f => ({
                ...f,
                cui: resp.cui || f.cui,
                legal_name: resp.legal_name || f.legal_name,
                trade_register: resp.trade_register || f.trade_register,
                county: resp.county || f.county,
                city: resp.city || f.city,
                street: fullStreet || f.street,
                postal_code: resp.postal_code || f.postal_code,
            }));
        } catch (e) {
            setAnafMsg('Nu am putut căuta CUI-ul în ANAF: ' + formatApiError(e));
        } finally {
            setAnafLoading(false);
        }
    };

    const fiscalChanged = () => {
        if (!data) return false;  // first save — counts as fresh, not a change
        return FISCAL_FIELDS.some(k => (data[k] || '') !== (form[k] || ''));
    };

    const save = async () => {
        setError(''); setInfo('');
        // Client-side hint — backend is authoritative.
        const required = ['legal_name', 'cui', 'trade_register', 'county', 'city', 'street', 'billing_email'];
        const missing = required.filter(k => !form[k]?.trim());
        if (missing.length) {
            setError('Câmpuri obligatorii lipsă: ' + missing.join(', '));
            return;
        }
        let password = null;
        // Only fiscal-identity changes require re-auth. First-time creation
        // (data == null or no prior CUI) is treated as "no fiscal change".
        if (data?.cui && fiscalChanged()) {
            password = window.prompt(
                'Schimbi date fiscale (CIF / denumire / nr. Reg. Com.). Confirmă cu parola contului tău:'
            );
            if (!password) return;
        }
        setSaving(true);
        try {
            const payload = { ...form };
            if (password) payload.password = password;
            const { data: resp } = await api.put('/r/billing', payload);
            const saved = resp.billing || form;
            setData(saved);
            setForm({ ...EMPTY, ...saved });
            setEditing(false);
            setInfo('Datele de facturare au fost salvate.');
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="panel"><div className="spinner" /></div>;
    }

    const hasAny = data && (data.cui || data.legal_name);

    return (
        <div data-testid="billing-profile-editor" style={{ maxWidth: 800 }}>
            <div className="kicker">Date facturare</div>
            <h2 className="headline" style={{ fontSize: '1.8rem', margin: '0.3rem 0 0.4rem' }}>
                Firma care primește facturile
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: 0, marginBottom: '1.4rem', lineHeight: 1.5 }}>
                Aceste date apar pe fiecare factură SmartBill emisă pentru abonament.
                Modificările la CIF, denumire sau nr. Reg. Com. cer confirmarea parolei.
            </p>

            {info && (
                <div className="panel" style={{ marginBottom: '1rem', borderColor: 'var(--neon-teal)', background: 'rgba(20,184,166,0.06)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <CheckCircle size={18} color="var(--neon-teal)" style={{ marginTop: 1, flexShrink: 0 }} />
                    <div style={{ fontSize: '0.9rem', color: 'var(--text)' }}>{info}</div>
                </div>
            )}

            {error && (
                <div className="panel" style={{ marginBottom: '1rem', borderColor: 'var(--neon-magenta)', background: 'rgba(255,46,146,0.06)', display: 'flex', gap: 10, alignItems: 'flex-start' }} data-testid="billing-error">
                    <AlertCircle size={18} color="var(--neon-magenta)" style={{ marginTop: 1, flexShrink: 0 }} />
                    <div style={{ fontSize: '0.88rem', color: 'var(--neon-magenta)' }}>{error}</div>
                </div>
            )}

            {!editing && (
                <div className="panel" style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem' }}>
                        <Building2 size={20} color="var(--amber)" />
                        <strong style={{ flex: 1 }}>{hasAny ? data.legal_name || '(fără denumire)' : 'Date facturare lipsă'}</strong>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={startEdit} data-testid="billing-edit">
                            <Edit3 size={14} /> {hasAny ? 'Editează' : 'Completează'}
                        </button>
                    </div>
                    {hasAny ? (
                        <Row cols={2}>
                            <ReadOnlyRow label="Forma juridică" value={data.entity_type} />
                            <ReadOnlyRow label="CUI" value={data.cui} />
                            <ReadOnlyRow label="Nr. Reg. Comerțului" value={data.trade_register} />
                            <ReadOnlyRow label="Județ" value={data.county} />
                            <ReadOnlyRow label="Localitate" value={data.city} />
                            <ReadOnlyRow label="Adresă" value={data.street} />
                            <ReadOnlyRow label="Cod poștal" value={data.postal_code} />
                            <ReadOnlyRow label="IBAN" value={data.bank_iban} />
                            <ReadOnlyRow label="Email facturare" value={data.billing_email} />
                            <ReadOnlyRow label="Telefon facturare" value={data.billing_phone} />
                        </Row>
                    ) : (
                        <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.88rem' }}>
                            Apasă „Completează" ca să adaugi datele firmei tale. Le poți căuta automat în registrul ANAF după CUI.
                        </p>
                    )}
                </div>
            )}

            {editing && (
                <div className="panel" data-testid="billing-form">
                    <Row cols={2}>
                        <Field label="Forma juridică *">
                            <select className="input" value={form.entity_type} onChange={set('entity_type')} data-testid="billing-entity-type">
                                {ENTITY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                        </Field>
                        <Field label="CUI * (caută în ANAF)">
                            <div style={{ display: 'flex', gap: 6 }}>
                                <input className="input" value={form.cui} onChange={set('cui')}
                                       placeholder="RO12345678" data-testid="billing-cui" />
                                <button type="button" className="btn btn-secondary btn-sm" onClick={runAnaf}
                                        disabled={anafLoading || !form.cui?.trim()} data-testid="billing-anaf">
                                    {anafLoading ? <span className="spinner" /> : <><Search size={14} /></>}
                                </button>
                            </div>
                            {anafMsg && <div style={{ fontSize: '0.8rem', color: anafMsg.includes('INACTIVĂ') ? 'var(--neon-magenta)' : 'var(--neon-teal)', marginTop: 4 }}>{anafMsg}</div>}
                        </Field>
                    </Row>

                    <Row cols={1}>
                        <Field label="Denumire juridică *">
                            <input className="input" value={form.legal_name} onChange={set('legal_name')}
                                   placeholder="ex: BURLIX SRL" data-testid="billing-legal-name" />
                        </Field>
                    </Row>

                    <Row cols={2}>
                        <Field label="Nr. Registrul Comerțului *">
                            <input className="input" value={form.trade_register} onChange={set('trade_register')}
                                   placeholder="J40/123/2020" data-testid="billing-trade-register" />
                        </Field>
                        <Field label="IBAN (opțional)">
                            <input className="input" value={form.bank_iban} onChange={set('bank_iban')}
                                   placeholder="RO49AAAA1B31..." data-testid="billing-iban" />
                        </Field>
                    </Row>

                    <Row cols={2}>
                        <Field label="Județ *">
                            <input className="input" value={form.county} onChange={set('county')} data-testid="billing-county" />
                        </Field>
                        <Field label="Localitate *">
                            <input className="input" value={form.city} onChange={set('city')} data-testid="billing-city" />
                        </Field>
                    </Row>

                    <Row cols={1}>
                        <Field label="Stradă, număr *">
                            <input className="input" value={form.street} onChange={set('street')} data-testid="billing-street" />
                        </Field>
                    </Row>

                    <Row cols={2}>
                        <Field label="Cod poștal (opțional)">
                            <input className="input" value={form.postal_code} onChange={set('postal_code')} data-testid="billing-postal" />
                        </Field>
                        <Field label="Email facturare *" hint="Pe acest email trimite SmartBill PDF-ul facturii.">
                            <input className="input" type="email" value={form.billing_email} onChange={set('billing_email')} data-testid="billing-email" />
                        </Field>
                    </Row>

                    <Row cols={2}>
                        <Field label="Telefon facturare (opțional)">
                            <input className="input" type="tel" value={form.billing_phone} onChange={set('billing_phone')} data-testid="billing-phone" />
                        </Field>
                    </Row>

                    {data?.cui && fiscalChanged() && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '0.7rem', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 8, marginBottom: 12 }}>
                            <ShieldAlert size={16} color="var(--amber)" style={{ marginTop: 1, flexShrink: 0 }} />
                            <div style={{ fontSize: '0.85rem', color: 'var(--text)' }}>
                                Modifici date fiscale (CIF / denumire / Reg. Com.) — la salvare ți se va cere parola contului tău.
                            </div>
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={cancelEdit} disabled={saving} data-testid="billing-cancel">
                            <X size={14} /> Anulează
                        </button>
                        <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={saving} data-testid="billing-save">
                            {saving ? <span className="spinner" /> : <><Save size={14} /> Salvează</>}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
